/**
 * Admin Results tab for HNS games.
 *
 * Renders:
 *   - Cumulative leaderboard from the standings the server sends with every
 *     game snapshot (`selectedGame.hnsStandings`)
 *   - Per-round breakdowns (newest first): each player's outcome, penalties
 *     and score, as the server built them in the admin Results snapshot
 *   - HnsResultsAuditLog (right column) from the same snapshot
 *
 * Live: re-fetches the admin Results snapshot when the game is rebroadcast
 * (wired in HnsContext via scheduleHnsAdminResultsRefresh).
 */

import { useEffect } from 'react';
import {
  getCheatTypeDisplayText,
  MEDALS,
  type AdminHnsResultsPlayer,
  type CheatType,
} from '@swtorbit/shared-socket-events';
import { formatDuration } from '@swtorbit/shared-react-hooks';
import { useGameContext } from '../../contexts/GameContext';
import { useHnsContext } from '../../contexts/HnsContext';
import { ResultsTabBase } from '../results';
import { HnsResultsAuditLog } from './HnsResultsAuditLog';
import { statusBadgeClass, leaderboardRowClass, HnsPathwaysSection } from '@swtorbit/shared-components';

function outcomeText(player: AdminHnsResultsPlayer): string {
  switch (player.outcome) {
    case 'seeker':
      return '🔍 Seeker';
    case 'disqualified':
      return `🚫 DQ — ${player.disqualificationReason
        ? getCheatTypeDisplayText(player.disqualificationReason as CheatType)
        : 'cheating'}`;
    case 'captured':
      return `🎯 Captured by ${player.capturedByName ?? 'unknown'}`;
    case 'escaped':
      return '🙈 Escaped';
    case 'hiding':
      return '🙈 Hiding';
  }
}

export function HnsResultsTab() {
  const { selectedGame } = useGameContext();
  const { hnsAdminResults, refreshHnsAdminResults } = useHnsContext();

  const selectedDbId = selectedGame?.dbId;
  useEffect(() => {
    if (selectedDbId != null) refreshHnsAdminResults();
  }, [selectedDbId, refreshHnsAdminResults]);

  const standings =
    selectedGame && selectedGame.rounds.some((r) => r.status === 'completed' && !r.disabled)
      ? selectedGame.hnsStandings
      : undefined;
  const isCompleted = selectedGame?.status === 'completed';

  return (
    <ResultsTabBase
      hasSelectedGame={!!selectedGame}
      game={selectedGame}
      title="🙈 Results"
      onRefresh={refreshHnsAdminResults}
      lastRefreshedUtcMs={hnsAdminResults?.generatedAtUtcMs}
    >
      {/* Cumulative leaderboard */}
      {standings && standings.leaderboard.length > 0 && (
        <div className={`p-lg border-md rounded-base${isCompleted ? ' border-gold glow glow-gold' : ' border-blue-light glow glow-blue-light'}`}>
          <div className="d-flex align-items-center justify-content-center mb-sm gap-sm">
            {isCompleted && <span className="fs-lg">🏆</span>}
            <h3 className={isCompleted ? 'text-gold' : 'text-white'}>
              {isCompleted ? 'Game Results' : 'Current Standings'}
            </h3>
          </div>
          <div className="d-flex flex-column gap-xs">
            {standings.leaderboard.map((player, idx) => (
              <div
                key={player.playerId}
                className={leaderboardRowClass(idx)}
              >
                <span className="leaderboard__rank">{idx < 3 ? MEDALS[idx] : `${idx + 1}.`}</span>
                <span className="leaderboard__name">{player.characterName}</span>
                <span className="leaderboard__stats">
                  Hider ×{player.roundsAsHider}
                  {player.timesCaptured > 0 && ` · Caught ×${player.timesCaptured}`}
                  {player.timesDisqualified > 0 && ` · DQ ×${player.timesDisqualified}`}
                </span>
                <span className="leaderboard__score">{formatDuration(player.totalScore)}</span>
              </div>
            ))}
          </div>
          <div className="leaderboard__footer">
            <p>Total Rounds: {standings.totalRounds}</p>
          </div>
        </div>
      )}

      {/* Snapshot body — rounds + audit log */}
      {!hnsAdminResults ? (
        <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
          Loading round details…
        </div>
      ) : (
        <div className="d-flex flex-column flex-lg-row gap-sm">
          {/* Per-round breakdowns */}
          <div className="d-flex flex-column gap-sm flex-1">
            {hnsAdminResults.rounds.length === 0 ? (
              <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
                No rounds have been created yet.
              </div>
            ) : (
              hnsAdminResults.rounds.map((round) => (
                <div
                  key={round.roundId}
                  className={`bg-space bg-o-50 text-white border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs${round.disabled ? ' o-50' : ''}`}
                >
                  <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
                    <h4 className="text-cyan">Round {round.roundNumber}</h4>
                    <span className={statusBadgeClass(round.status)}>
                      {round.status.toUpperCase()}
                      {round.disabled && ' (DISABLED)'}
                    </span>
                  </div>

                  {round.status === 'completed' && !round.scoresSaved && round.players.length > 0 && (
                    <span className="text-slate fs-sm">
                      Scores were not saved when this round ended; these are worked out from its timeline.
                    </span>
                  )}

                  {round.players.length === 0 ? (
                    <span className="text-cyan fs-sm">No player data for this round.</span>
                  ) : (
                    <div className="d-flex flex-column gap-2xs">
                      {round.players.map((player) => (
                        <div key={player.playerId} className="d-flex justify-content-between gap-xs align-items-center">
                          <span className="text-white">{player.characterName}</span>
                          <span className="text-cyan fs-sm">
                            {outcomeText(player)}
                            {player.penaltySeconds > 0 && ` · −${player.penaltySeconds}s penalty`}
                            {player.originalRole !== 'seeker' && player.outcome !== 'hiding' &&
                              ` · ${formatDuration(player.scoreSeconds)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Audit log */}
          <div className="flex-1">
            <HnsResultsAuditLog results={hnsAdminResults} />
          </div>
        </div>
      )}

      {/* Each player's route through a finished round (Krall's Accord). */}
      {selectedGame && (
        <div className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs">
          <h4 className="text-cyan">Pathways</h4>
          <HnsPathwaysSection game={selectedGame} gameId={selectedGame.dbId} />
        </div>
      )}
    </ResultsTabBase>
  );
}
